/* The per-integration connect snippets, shared by the Quick start page's
 * connect step and an agent's Connect tab. Each integration knows how to build its
 * code snippet(s) from the gateway URL + agent id, plus the one caveat that
 * changes what the reader types. Anything that speaks the Anthropic Messages,
 * OpenAI or Gemini API works; these are the common stacks, and "generic" is the
 * hand-configuration guide for everything else.
 *
 * ⚠️ `ccCustomHeaders` and `ccSettings` are mirrored by the `@ai21/gateway` CLI,
 * pinned there as a captured snapshot (ai21-intelligent-gateway-cli,
 * test/fixtures/webapp-snippets.json). Editing them here means re-capturing that
 * fixture — nothing fails when this file moves first, so the CLI would keep
 * writing values this page no longer recommends.
 *
 * ⚠️ That fixture also pins a `genericCurl` this page no longer has (OPSS-570
 * replaced the paste-me curl with a guide). The CLI still builds one for its
 * verifying ping; it is now unmirrored, and its `<your-anthropic-api-key>`
 * placeholder carries the same paste-and-fail bug this change removed here. */

import { WarningCircle } from "@phosphor-icons/react";
import { useState, type ReactNode } from "react";
import { kindSpec, type AgentIntegration } from "../../agents";
import { CodeBlock, Select } from "../../design-system";
import { DropdownSelect } from "../../components/controls/DropdownSelect";
import { SUPPORT_EMAIL } from "../welcome/waiting";
import { DocExternalLink, DocNote, DocText, InlineCode } from "./primitives";
import {
  AGENT_ID_PLACEHOLDER,
  AI21_KEY_PLACEHOLDER,
  useSnippetContext,
  useSnippetHighlights,
  type SnippetInputs,
} from "./snippetContext";
import {
  DEFAULT_LANGCHAIN_CLIENT,
  LANGCHAIN_CLIENTS,
  langchainTabs,
  type LangChainClient,
} from "./langchainSnippets";

/** Snippet-integration slugs — defined by the agent-kind registry, since each
 * kind declares which integration's snippets fit it. */
export type IntegrationType = AgentIntegration;

export interface Integration {
  type: IntegrationType;
  label: string;
}

/** The integration types offered in the picker; "generic" is the catch-all. */
export const INTEGRATIONS: Integration[] = [
  { type: "claude-code", label: "Claude Code" },
  { type: "langchain", label: "LangChain" },
  { type: "codex", label: "Codex" },
  { type: "generic", label: "Other / custom" },
];

/** The integration that best matches an agent kind (from the kind registry). */
export function integrationForKind(kind: string): IntegrationType {
  return kindSpec(kind).integration;
}

/** Dropdown to choose which integration's snippets to show. */
export function IntegrationPicker({
  value,
  onChange,
}: {
  value: IntegrationType;
  onChange: (type: IntegrationType) => void;
}) {
  return (
    <Select
      label="Integration"
      value={value}
      onChange={(e) => onChange(e.target.value as IntegrationType)}
      className="max-w-sm"
    >
      {INTEGRATIONS.map((i) => (
        <option key={i.type} value={i.type}>
          {i.label}
        </option>
      ))}
    </Select>
  );
}

/* Deep links into the customer-facing docs site, per integration. The site
 * isn't deployed yet, so the URL is null and nothing renders — a "docs coming
 * soon" line is noise in a modal the reader is trying to get out of. Filling
 * the URL in turns it into a real link with no other change. */
const DOCS_REFERENCES: Partial<
  Record<IntegrationType, { title: string; url: string | null }>
> = {
  langchain: { title: "LangChain setup guide", url: null },
};

/** Pointer to the full guide for an integration, under its snippet. Renders
 *  nothing until that guide has a real URL to link. */
function DocsReference({ type }: { type: IntegrationType }) {
  const reference = DOCS_REFERENCES[type];
  if (!reference?.url) return null;

  return (
    <DocNote>
      Full reference:{" "}
      <DocExternalLink href={reference.url}>{reference.title}</DocExternalLink>
    </DocNote>
  );
}

/** Dropdown to choose which LangChain client the snippet constructs; the
 *  language (python / typescript) is the CodeBlock's own tab strip. Options are
 *  grouped by construction style (provider class vs the universal initializer),
 *  so each Claude/GPT pair reads as one choice. */
function LangChainClientPicker({
  value,
  onChange,
}: {
  value: LangChainClient;
  onChange: (client: LangChainClient) => void;
}) {
  return (
    <DropdownSelect
      label="Client"
      value={value}
      options={LANGCHAIN_CLIENTS}
      onChange={onChange}
      className="max-w-sm"
    />
  );
}

/** Claude Code's custom-headers value, shared by the shell and settings forms. */
function ccCustomHeaders(
  agentId: string,
  ai21Key: string,
  userId: string,
): string {
  return `x-ai21-key: ${ai21Key}\nx-agent-id: ${agentId}\nx-ai21-user-id: ${userId}`;
}

function ccShell({
  gatewayUrl,
  agentId,
  ai21Key,
  userId,
}: SnippetInputs): string {
  // No ANTHROPIC_API_KEY: Claude Code manages its own Anthropic credential,
  // so users only set the base URL and attribution headers here.
  return [
    `export ANTHROPIC_BASE_URL=${gatewayUrl}`,
    `export ANTHROPIC_CUSTOM_HEADERS="${ccCustomHeaders(agentId, ai21Key, userId)}"`,
    `export ENABLE_TOOL_SEARCH=true`,
  ].join("\n");
}

function ccSettings({
  gatewayUrl,
  agentId,
  ai21Key,
  userId,
}: SnippetInputs): string {
  return JSON.stringify(
    {
      // No ANTHROPIC_API_KEY: Claude Code supplies its own Anthropic credential.
      env: {
        ANTHROPIC_BASE_URL: gatewayUrl,
        ANTHROPIC_CUSTOM_HEADERS: ccCustomHeaders(agentId, ai21Key, userId),
        ENABLE_TOOL_SEARCH: "true",
      },
    },
    null,
    2,
  );
}

function codexPrompt({
  gatewayUrl,
  agentId,
  ai21Key,
  userId,
}: SnippetInputs): string {
  return `Update ~/.codex/config.toml without changing other settings. Set root model_provider="ai21". Add or replace model_providers.ai21 with name="AI21 Gateway", base_url="${gatewayUrl}/v1", wire_api="responses", requires_openai_auth=true, and HTTP headers x-upstream="openai-codex", x-ai21-key="${ai21Key}", x-agent-id="${agentId}", x-ai21-user-id="${userId}".`;
}

function codexPlacementExample({
  gatewayUrl,
  agentId,
  ai21Key,
  userId,
}: SnippetInputs): string {
  // base_url ends in /v1 — Codex appends /responses itself. The dedicated
  // openai-codex backend forwards Codex's ChatGPT login credential to the
  // ChatGPT product endpoint without Responses schema translation.
  //
  // Every line here is safe to paste: the surrounding config is described in
  // comments rather than shown as real keys, so copying this can't overwrite a
  // setting the reader never chose. The root-key note matters — TOML would read
  // `model_provider` as part of whichever [table] precedes it.
  return [
    "# Adds to your existing ~/.codex/config.toml — nothing else changes.",
    "# Keep model_provider above every [section]: it is a root key.",
    `model_provider = "ai21"`,
    "",
    "[model_providers.ai21]",
    `name = "AI21 Gateway"`,
    `base_url = "${gatewayUrl}/v1"`,
    `wire_api = "responses"`,
    `requires_openai_auth = true`,
    "[model_providers.ai21.http_headers]",
    `x-upstream = "openai-codex"`,
    `x-ai21-key = "${ai21Key}"`,
    `x-agent-id = "${agentId}"`,
    `x-ai21-user-id = "${userId}"`,
  ].join("\n");
}

/* The three headers every "other" agent must send. The only thing the generic
 * guide offers for copying — a curl would re-create the "paste this and it
 * works" expectation that the guide exists to replace.
 *
 * `x-ai21-user-id` is listed as required by product decision, not by the wire:
 * the api-server reads it for per-user attribution and does not forward it, so
 * a request without it still succeeds (language-studio-api-server
 * services/tokenwise_service.py — absent from _FORWARDED_INBOUND_HEADERS). */
function genericHeaders({ agentId, ai21Key, userId }: SnippetInputs): string {
  return [
    `x-ai21-key: ${ai21Key}`,
    `x-agent-id: ${agentId}`,
    `x-ai21-user-id: ${userId}`,
  ].join("\n");
}

/* Where "contact us" in the generic guide's unsupported-harness note points.
 * ⚠️ Interim: the support mailbox, so the CTA works today. A dedicated
 * onboarding-help link is coming — replace this one value when it lands. */
const CONTACT_US_HREF = `mailto:${SUPPORT_EMAIL}`;

/* Harnesses the generic guide cannot onboard by hand. They need their own
 * client-side wiring (the gateway tap) rather than a base URL and headers, so
 * pointing their config at the gateway does not connect them. */
const UNSUPPORTED_HARNESSES = "Cursor, Claude Desktop or GitHub Copilot";

/* A numbered step of the generic guide. Deliberately not the docs `Step`
 * primitive: its badge gutter costs 38px of the onboarding card's 384px content
 * column (AddAgentModal), and its text-h6 title wraps to two lines at that
 * width. A run-in number keeps the sequence and gives the body the full column. */
function GuideStep({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <li className="flex flex-col gap-2">
      {/* font-medium, not font-semibold: both type families stop at 500 and a
          browser-synthesised 600 is a design-system prohibition. */}
      <h3 className="text-body-sm font-medium text-fg-1">
        <span className="text-fg-3">{number}.</span> {title}
      </h3>
      {children}
    </li>
  );
}

/* The base URL differs by wire format because each SDK appends a different
 * path: the Anthropic SDK appends the whole /v1/messages, the OpenAI one only
 * /chat/completions, so that one needs the /v1 already in the base. Getting it
 * wrong is a 404, not an auth error — which is why this is a table the reader
 * can find their own client in, and not a sentence. */
function BaseUrlRows({ gatewayUrl }: { gatewayUrl: string }) {
  const rows: { speaks: string; url: string }[] = [
    { speaks: "Anthropic Messages", url: gatewayUrl },
    { speaks: "OpenAI Chat Completions or Responses", url: `${gatewayUrl}/v1` },
    { speaks: "Google Gemini (generateContent)", url: gatewayUrl },
  ];

  /* Stacked, not two columns: the onboarding card pins this to a 384px content
   * column (AddAgentModal), and a Tailwind `sm:` breakpoint reads the viewport,
   * not that container — so a side-by-side row survives here and squeezes the
   * URL until it breaks mid-token. The label is short; the URL gets the width. */
  return (
    // rounded-sm is the 4px card/control radius; rounded-md (8px) is reserved
    // for modals and drawers. gap-2 keeps the 8px spacing step.
    <dl className="flex max-w-2xl flex-col gap-2 rounded-sm border p-3">
      {rows.map(({ speaks, url }) => (
        <div key={speaks} className="flex flex-col gap-1">
          <dt className="text-body-sm text-fg-2">{speaks}</dt>
          <dd className="min-w-0 break-all">
            <InlineCode>{url}</InlineCode>
          </dd>
        </div>
      ))}
    </dl>
  );
}

/* Renders the snippet(s) + caveat for one integration, personalized from the
 * shared snippet context (gateway URL, selected/pinned agent).
 *
 * `agentId` may be passed to override the context's selected agent — the
 * welcome wizard does this because right after registering an agent the agents
 * query is refetching, so the context `agent` is briefly null and the snippet
 * would flash the placeholder id. Passing the known id keeps it stable. */

export function IntegrationSnippets({
  type,
  agentId: agentIdOverride,
  onCopy,
  showRestartNote = true,
}: {
  type: IntegrationType;
  agentId?: string;
  /** Fired after a successful copy of any snippet block (analytics hook). */
  onCopy?: () => void;
  /** Pass false where a surrounding step already tells the user to restart
   *  (the wizard's connect step); defaults to true elsewhere. */
  showRestartNote?: boolean;
}) {
  const { gatewayUrl, isPlaceholderUrl, agent, ai21Key, userId } =
    useSnippetContext();
  const highlights = useSnippetHighlights();
  // LangChain-only second axis (which chat model class the reader constructs);
  // held here so every surface that renders the snippets gets the picker.
  const [langchainClient, setLangchainClient] = useState<LangChainClient>(
    DEFAULT_LANGCHAIN_CLIENT,
  );
  const [codexExampleExpanded, setCodexExampleExpanded] = useState(false);
  const agentId = agentIdOverride ?? agent?.agent_id ?? AGENT_ID_PLACEHOLDER;
  const inputs: SnippetInputs = {
    gatewayUrl,
    agentId,
    ai21Key: ai21Key ?? AI21_KEY_PLACEHOLDER,
    userId,
  };

  const placeholderNote = isPlaceholderUrl ? (
    <DocNote>
      <InlineCode>{gatewayUrl}</InlineCode> is a placeholder — replace it with
      your gateway&apos;s URL.
    </DocNote>
  ) : null;

  let blocks: ReactNode;
  if (type === "claude-code") {
    blocks = (
      <>
        <DocText>
          Drop your custom snippet into{" "}
          <InlineCode>.claude/settings.json</InlineCode> (project or user), or
          as shell environment variables. The AI21 key goes in{" "}
          <InlineCode>ANTHROPIC_CUSTOM_HEADERS</InlineCode>.
          {showRestartNote &&
            " Then restart any open Claude Code sessions — settings are read at startup."}
        </DocText>
        <CodeBlock
          tabs={[
            { label: ".claude/settings.json", code: ccSettings(inputs) },
            { label: "shell", code: ccShell(inputs) },
          ]}
          highlights={highlights}
          onCopied={onCopy}
          wrap
          maxLines={12}
        />
        {placeholderNote}
      </>
    );
  } else if (type === "langchain") {
    blocks = (
      <>
        <LangChainClientPicker
          value={langchainClient}
          onChange={setLangchainClient}
        />
        {/* Figma 673:920 puts this between the picker and the snippet, and keeps
            it to one line — the picker below it is self-evident. */}
        <p className="text-[11px] text-[var(--text-muted)]">
          Choose the AI client your agent uses, then add the snippet to its
          configuration.
        </p>
        {/* A LangChain constructor runs 12–15 lines, so this clamp still
            scrolls a little — it's the tallest snippet of the four. */}
        <CodeBlock
          tabs={langchainTabs(langchainClient, inputs)}
          highlights={highlights}
          onCopied={onCopy}
          wrap
          maxLines={12}
        />
        {placeholderNote}
        {showRestartNote && (
          <DocText>
            Re-run your agent — LangChain reads the base URL and headers when
            the client is constructed.
          </DocText>
        )}
        <DocsReference type={type} />
      </>
    );
  } else if (type === "codex") {
    blocks = (
      <>
        <DocText>
          Copy this prompt into Codex, then sign in with{" "}
          <InlineCode>codex login</InlineCode>. Codex sends your ChatGPT login
          through the gateway; no OpenAI API key is needed.
        </DocText>
        <CodeBlock
          label="Prompt"
          copyLabel="Copy prompt"
          code={codexPrompt(inputs)}
          highlights={highlights}
          onCopied={onCopy}
          wrap
        />
        {placeholderNote}
        {showRestartNote && (
          <DocText>
            Restart open Codex sessions after it updates the file.
          </DocText>
        )}
        <DocText>
          Or add it to <InlineCode>~/.codex/config.toml</InlineCode> yourself:
        </DocText>
        <CodeBlock
          code={codexPlacementExample(inputs)}
          copyLabel="Copy config"
          scrollable={false}
          filled={false}
          collapsedLines={0}
          expanded={codexExampleExpanded}
          onExpandedChange={setCodexExampleExpanded}
          collapsedLabel="See example"
          expandedLabel="Hide example"
          wrap
        />
      </>
    );
  } else {
    blocks = (
      <>
        {/* Three steps, not four: the headers used to be a step each and then
            repeat in the copy block below them. The block is the instruction. */}
        <ol className="flex flex-col gap-4">
          <GuideStep number={1} title="Point your client at the gateway">
            {/* Above the table, not under it: a reader on one of these gets
                turned away before working through a URL that won't help.
                Shaped like the wizard's "No request yet" card (AgentTrafficCard)
                — filled WarningCircle, title row, muted line under it — since
                the two appear in the same flow. Amber tint/val is the "needs
                attention" pair, and the one that remaps for the maroon
                onboarding ground where the Reconnect drawer renders this. */}
            <div
              className="flex flex-col gap-2 rounded-sm border p-4"
              style={{
                background: "var(--tint-amber)",
                borderColor: "var(--val-amber)",
              }}
            >
              <div className="flex items-center gap-2">
                <WarningCircle
                  size={16}
                  weight="fill"
                  className="flex-shrink-0"
                  style={{ color: "var(--val-amber)" }}
                />
                {/* text-xs, not text-body-sm: the DS type utilities have no
                    fontSize entry in tailwind.config and emit nothing, so this
                    is the only way to actually set 12px — the scale's floor. */}
                <span
                  className="text-xs font-medium"
                  style={{ color: "var(--val-amber)" }}
                >
                  Not supported for {UNSUPPORTED_HARNESSES}
                </span>
              </div>
              <p className="text-xs text-fg-2">
                If you use one of those and need help onboarding,{" "}
                <DocExternalLink href={CONTACT_US_HREF}>
                  contact us
                </DocExternalLink>
                .
              </p>
            </div>
            <BaseUrlRows gatewayUrl={gatewayUrl} />
            {placeholderNote}
          </GuideStep>

          {/* All three required, and the block says so by listing only them:
              `x-agent-id` flips the agent's `used` latch on its first request,
              so without it the connect step waits forever. */}
          <GuideStep number={2} title="Add three headers">
            <CodeBlock
              label="headers"
              code={genericHeaders(inputs)}
              highlights={highlights}
              onCopied={onCopy}
              wrap
            />
            {/* A fourth header, but only sometimes — so it sits under the block
                rather than in it. The proxy reads the provider off the model id
                and falls back to Anthropic, so a `gemini-*` id silently reaches
                the wrong provider without this (_resolve_upstream in
                language-studio-api-server services/tokenwise_service.py). */}
            <DocNote>
              The provider is read from the model id. Anything that isn&apos;t{" "}
              <InlineCode>claude-*</InlineCode> or{" "}
              <InlineCode>gpt-*</InlineCode> — Gemini included — also needs{" "}
              <InlineCode>x-upstream</InlineCode>:{" "}
              <InlineCode>anthropic</InlineCode>,{" "}
              <InlineCode>openai</InlineCode> or <InlineCode>gemini</InlineCode>
              .
            </DocNote>
          </GuideStep>
        </ol>
      </>
    );
  }

  return <div className="flex flex-col gap-3">{blocks}</div>;
}
